import React, { useEffect, useState, useMemo, use } from "react";
import Sidebar from "../../components/Sidebar/Sidebar";
import Footer from "../../components/Footer/Footer";
import { Download } from "lucide-react";
import MedicalHistoryReport from "../../components/PdfExport/PdfExport";
import { PDFDownloadLink, Document, Page, Text } from '@react-pdf/renderer';
import { getMedications } from "../../api/medicationsApi";
import { fetchUserEvents } from "../../components/calendar/calendarAPI";
import { getUserInfo } from "../../api/userApi";
import { getSurgeries } from "../../api/surgeryApi";
import { getMedicalHistory } from "../../api/medicalHistoryApi";
import { fetchAllergies } from "../../api/healthDataApi";
import { getFilesByUserId } from "../../api/filesApi";
import { Link } from "react-router-dom";
import { getObservationsByCategory } from "../../api/observationsApi";

export default function MedicalHistory() {
  const [filter, setFilter] = useState("all");
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [historyData, setHistoryData] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [vitalsData, setVitalsData] = useState([]);
  const [labResults, setLabResults] = useState([]);
  const [selectedVitals, setSelectedVitals] = useState([]);
  const [selectedLabs, setSelectedLabs] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [advanceDirectives, setAdvanceDirectives] = useState([]);

  const sortedHistory = useMemo(() => {
    const matchesFilter = (item) => filter === "all" || item.type === filter;

    const withDates = historyData
      .filter(item => item.date && item.date !== "N/A" && matchesFilter(item))
      .sort((a, b) => new Date(b.date) - new Date(a.date));

    const noDates = historyData
      .filter(item => (!item.date || item.date === "N/A") && matchesFilter(item))
      .sort((a, b) => a.type.localeCompare(b.type));

    return { withDates, noDates };
  }, [historyData, filter]);

  const userId = localStorage.getItem("userId");

  useEffect(() => {
    const fetchAllHistory = async () => {
      const [meds, appts, surgeries, histories] = await Promise.all([
        getMedications(userId),
        fetchUserEvents(userId),
        getSurgeries(userId),
        getMedicalHistory(userId)
      ]);

      const formattedMeds = meds.map(m => ({
        type: "Medications",
        date: new Date(m.start_date).toLocaleDateString(),
        title: m.drug_name,
        details: `Prescribed ${m.dosage} ${m.drug_name.toLowerCase()} for ${m.condition_treated.toLowerCase()}`
      }));

      const formattedAppts = appts.map(a => ({
        type: "Appointments",
        date: new Date(a.date).toLocaleDateString(),
        title: a.event_title,
        details: a.notes || `${a.type} appointment`
      }));

      const formattedSurgeries = surgeries.map(s => ({
        type: "Surgeries",
        date: s.surgery_date ? new Date(s.surgery_date).toLocaleDateString() : "N/A",
        title: s.surgery_name,
        details: "Surgical procedure"
      }));

      const formattedHistories = histories.map(h => ({
        type: "Conditions",
        date: h.diagnosis_date ? new Date(h.diagnosis_date).toLocaleDateString() : "N/A",
        title: h.condition_name,
        details: `Previously resolved condition`
      }));

      setHistoryData([...formattedMeds, ...formattedAppts, ...formattedSurgeries, ...formattedHistories]);
    };

    fetchAllHistory();
  }, [userId]);


  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const data = await getUserInfo(userId);
        setUserInfo(data);
      } catch (error) {
        console.error(error);
      }
    }

    fetchUserInfo();
  }, [userId])

  useEffect(() => {
    const patientId = localStorage.getItem("patientId");
    if (!patientId) {
      console.error("No patient ID found in localStorage");
      return;
    }
  
    const fetchPatientData = async () => {
      try {
        // Fetch vitals from your DB
        const vitals = await getObservationsByCategory(patientId, "vital");
        
        // Fetch labs from your DB
        const labs = await getObservationsByCategory(patientId, "lab");
  
        setVitalsData(vitals);
        setLabResults(labs);
  
      } catch (error) {
        console.error("Error fetching observations from DB:", error);
      }
    };
  
    fetchPatientData();
  }, []);

  useEffect(() => {
    const getAllergies = async () => {
      try {
        const response = await fetchAllergies(userId);
        setAllergies(response);
      } catch (error) {
        console.error("Error fetching allergies:", error);
      }
    };
    getAllergies();
  }, [userId]);

  useEffect(() => {
    const fetchAdvanceDirectives = async () => {
      try {
        const filesData = await getFilesByUserId(userId);
        const directives = filesData["Advance Directives"] || [];
        setAdvanceDirectives(directives);
      } catch (error) {
        console.error("Error fetching advance directives:", error);
      }
    };
    fetchAdvanceDirectives();
  }, [userId]);

  return (
    <div className={`ml-0 flex flex-col min-h-screen transition-all duration-300 ${sidebarExpanded ? 'md:ml-56' : 'md:ml-16'}`}>
      <Sidebar expanded={sidebarExpanded} setExpanded={setSidebarExpanded} />
      <main className="flex-1 p-6 overflow-auto bg-gray-100">
        <h1 className="mb-6 text-3xl font-bold text-[#04395b]">
          Medical History & Export
        </h1>

        {/* filter */}
        <div className="flex flex-wrap items-center gap-4 mb-8">
          <label className="text-lg font-medium text-[#04395b]">Filter:</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="all">All</option>
            <option value="Appointments">Appointments</option>
            <option value="Medications">Medications</option>
            <option value="Surgeries">Surgeries</option>
            <option value="Conditions">Conditions</option>
          </select>
        </div>

        {/* timeline */}
        <section className="mb-12">
          {/* Records with dates */}
          {sortedHistory.withDates.length === 0 && sortedHistory.noDates.length === 0 ? (
            <p className="text-gray-600 italic">No records found for the selected filters.</p>
          ) : (
            <>
              {/* Dated records */}
              <div className="relative border-l-2 border-[#04395b] pl-6">
                {sortedHistory.withDates.map((item, index) => (
                  <div key={index} className="mb-8">
                    <div className="absolute w-4 h-4 bg-[#04395b] rounded-full -left-2 top-1.5"></div>
                    <p className="text-sm text-gray-500">{item.date}</p>
                    <h3 className="text-lg font-semibold text-[#04395b]">
                      {item.title} <span className="ml-2 text-sm font-normal text-gray-500">({item.type})</span>
                    </h3>
                    <p className="text-gray-700">{item.details}</p>
                  </div>
                ))}
              </div>

              {/* Divider for undated */}
              {sortedHistory.noDates.length > 0 && (
                <>
                  <h3 className="mt-8 mb-4 text-lg font-semibold text-[#04395b]">Undated Records</h3>
                  <div className="relative border-l-2 border-gray-400 pl-6">
                    {sortedHistory.noDates.map((item, index) => (
                      <div key={index} className="mb-8">
                        <div className="absolute w-4 h-4 bg-gray-400 rounded-full -left-2 top-1.5"></div>
                        <p className="text-sm text-gray-500">N/A</p>
                        <h3 className="text-lg font-semibold text-[#04395b]">
                          {item.title} <span className="ml-2 text-sm font-normal text-gray-500">({item.type})</span>
                        </h3>
                        <p className="text-gray-700">{item.details}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </section>

        {/* allergies */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-[#04395b] mb-4">Allergies</h2>
          {allergies.length === 0 ? (
            <p className="text-gray-600 italic">No allergies found.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {allergies.map((a, index) => (
                <span
                  key={index}
                  className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium border border-blue-300 shadow-sm"
                >
                  {a}
                </span>
              ))}
            </div>
          )}
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-[#04395b] mb-3">Advance Directives</h2>

          {advanceDirectives.length === 0 ? (
            <p className="text-gray-600 italic">
              No advance directives on file. You can upload them in the{" "}
              <Link to="/results" className="text-blue-600 hover:underline">
                Results & Records
              </Link>{" "}
              page.
            </p>
          ) : (
            <p className="text-gray-700">
              You have <span className="font-medium">{advanceDirectives.length}</span> advance directive
              {advanceDirectives.length > 1 ? " documents" : " document"} on file. You can view or download them from the{" "}
              <Link to="/results" className="text-blue-600 hover:underline">
                Results & Records
              </Link>{" "}
              page.
            </p>
          )}
        </section>


        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-[#04395b] mb-4">Select Vitals for Export</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {vitalsData.length === 0 ? (
              <p className="text-gray-600 italic">No vitals found.</p>) : (vitalsData.map((v) => (
                <label
                  key={v.id}
                  className="flex items-center gap-2 border p-2 rounded-md bg-white shadow-sm hover:shadow-md transition cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4"
                    checked={selectedVitals.includes(v.id)}
                    onChange={() =>
                      setSelectedVitals((prev) =>
                        prev.includes(v.id) ? prev.filter(id => id !== v.id) : [...prev, v.id]
                      )
                    }
                  />
                  <div className="text-sm leading-tight">
                    <p className="text-gray-600">{new Date(v.effective_date).toLocaleDateString()}</p>
                    <p className="font-medium text-gray-800">{v.display_name}</p>
                  </div>
                </label>
              )))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-[#04395b] mb-4">Select Lab Results for Export</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {labResults.length === 0 ? (
              <p className="text-gray-600 italic">No lab results found.</p>
            ) : (
              labResults.map((l) => (
                <label
                  key={l.id}
                  className="flex items-center gap-2 border p-2 rounded-md bg-white shadow-sm hover:shadow-md transition cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4"
                    checked={selectedLabs.includes(l.id)}
                    onChange={() =>
                      setSelectedLabs((prev) =>
                        prev.includes(l.id) ? prev.filter(id => id !== l.id) : [...prev, l.id]
                      )
                    }
                  />
                  <div className="text-sm leading-tight">
                    <p className="text-gray-600">{new Date(l.effective_date).toLocaleDateString()}</p>
                    <p className="font-medium text-gray-800">{l.display_name}</p>
                  </div>
                </label>
              )))}
          </div>
        </section>

        {/* export options */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-[#04395b]">Export Options</h2>
          <p className="mb-4 text-gray-700">
            Generate and send a summary report of your medical history including recent test results, medications, and ongoing treatments.
          </p>
          <div className="flex flex-wrap gap-4">
            {userInfo && historyData.length > 0 && (
              <PDFDownloadLink
                key={Date.now()}
                document={
                  <MedicalHistoryReport
                    history={historyData}
                    user={{
                      name: `${userInfo[0].first_name} ${userInfo[0].last_name}`,
                      dob: userInfo[0].dob,
                      email: userInfo[0].email,
                      phone: userInfo[0].phone_number
                    }}
                    vitals={vitalsData.filter(v => selectedVitals.includes(v.id))}
                    labs={labResults.filter(l => selectedLabs.includes(l.id))}
                    allergies={allergies}
                  />
                }
                fileName="medical_history_report.pdf"
                className="flex items-center gap-2 px-5 py-3 font-medium text-white bg-[#04395b] rounded-lg hover:bg-[#022b42]"
              >
                {({ loading }) => (
                  <>
                    <Download size={16} />
                    {loading ? "Generating PDF..." : "Export PDF"}
                  </>
                )}
              </PDFDownloadLink>
            )}


            <a href="#" className="px-5 py-3 font-medium text-white bg-[#04395b] rounded-lg hover:bg-[#022b42]">
              Send via Encrypted Email
            </a>
            <a href="#" className="px-5 py-3 font-medium text-white bg-[#04395b] rounded-lg hover:bg-[#022b42]">
              Share via App
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}